// What a wallet is known by in the money windows — the header's wallet window,
// Deposit, Withdraw: the primary's star.
//
//   PrimaryStar   the primary wallet's mark, after its name: a gold star with
//                 rounded points (round 9, the operator: "a star, not the dot —
//                 and a pleasant one"). One drawing for every money window.
//
// (Round 9 took the window's address chip — the short address as one copy
// control that opened the whole address under the pointer — with the window's
// redo: each wallet's row now carries its address and its copy.)
//
// The face: a round plate in one of five quiet tones, picked by the address,
// with the first letter of the wallet's name.

import { useId } from 'react'
import { Described } from '../ui/Described'
import s from './wallet.a.module.css'

// five points, the inner ones at a little under half the outer radius, in a
// 16 box; drawn with a round join of its own colour, so every point is soft
const STAR =
  'M8 2L9.82 5.89L14.09 6.42L10.95 9.36L11.76 13.58L8 11.5L4.24 13.58L5.05 9.36L1.91 6.42L6.18 5.89Z'

/** The primary wallet's mark: named once ("Primary wallet"), drawn as a
 *  gold star — a light gold at its top, the token's gold at its foot. The
 *  drawing itself is hidden; its plate is the image. */
export function PrimaryStar({
  size = 13,
  title = 'Primary wallet',
  className,
}: {
  size?: number
  /** Accessible detail; the name stays "Primary wallet". */
  title?: string
  className?: string
}) {
  // one gradient per star: a list of wallets draws several
  const gold = `primary-star-${useId().replace(/:/g, '')}`
  return (
    <Described
      as="span"
      className={`${s.primaryStar} ${className ?? ''}`}
      role="img"
      aria-label="Primary wallet"
      description={title}
      data-part="primary"
    >
      <svg width={size} height={size} viewBox="0 0 16 16" aria-hidden="true" focusable="false">
        <defs>
          <linearGradient id={gold} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#ffdc6e" />
            <stop offset="1" stopColor="#f5b31f" />
          </linearGradient>
        </defs>
        <path
          d={STAR}
          fill={`url(#${gold})`}
          stroke={`url(#${gold})`}
          strokeWidth="1.6"
          strokeLinejoin="round"
        />
      </svg>
    </Described>
  )
}

// (the stand's five tones, a step lighter in the ground)
const FACE_TONES = [
  { bg: '#293152', ink: '#9db2ff' },
  { bg: '#322b54', ink: '#b6a8ff' },
  { bg: '#1c3a2c', ink: '#66d9a5' },
  { bg: '#3d3220', ink: '#f0be62' },
  { bg: '#40271f', ink: '#f59a75' },
] as const

function hash32(text: string): number {
  let h = 0x811c9dc5
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i)
    h = Math.imul(h, 0x01000193)
  }
  return h >>> 0
}

/** The first letter or digit of a wallet's name, for its face. */
export function initialOf(label: string): string {
  const first = Array.from(label.trim()).find((ch) => /[\p{L}\p{N}]/u.test(ch))
  return (first ?? 'W').toUpperCase()
}

export function WalletFace({
  address,
  label,
  large = false,
}: {
  address: string
  label: string
  large?: boolean
}) {
  const tone = FACE_TONES[(hash32(address) >>> 24) % FACE_TONES.length] ?? FACE_TONES[0]
  return (
    <span
      className={s.face}
      data-face="letter"
      data-large={large || undefined}
      style={{ background: tone.bg, color: tone.ink }}
      aria-hidden="true"
    >
      {initialOf(label)}
    </span>
  )
}
